import { useState } from 'react';
import type { Mailboxes, NavItem } from '../../mail/mailboxes';
import logo from '../../assets/logo.png';
import { Button } from '../../ui';

interface Props {
	mailboxes: Mailboxes;
	current: string;
	onSelect: (item: NavItem) => void;
	onCompose: () => void;
	onSettings: () => void;
	onSignOut: () => void;
	email: string;
}

const MORE_KEY = 'ks-sidebar-more';

function readMoreOpen(): boolean {
	try {
		return '1' === localStorage.getItem(MORE_KEY);
	} catch {
		return false;
	}
}

/**
 * The sidebar: Compose, the four everyday views, and everything else behind
 * "More" -- see mail/mailboxes.ts for why. Every entry has a written label; the
 * glyphs are decoration, never the only way to tell what something is.
 */
export function FolderList({ mailboxes, current, onSelect, onCompose, onSettings, onSignOut, email }: Props) {
	const inMore = mailboxes.more.some(item => item.id === current);
	const [moreOpen, setMoreOpen] = useState(readMoreOpen);
	// Never hide the view you are looking at.
	const showMore = moreOpen || inMore;

	function toggleMore() {
		const next = !showMore;
		setMoreOpen(next);
		try {
			localStorage.setItem(MORE_KEY, next ? '1' : '0');
		} catch {
			// Private mode: the choice just isn't remembered.
		}
	}

	const entry = (item: NavItem) => (
		<button
			key={item.id}
			className="folder"
			aria-current={item.id === current}
			onClick={() => onSelect(item)}
		>
			<span className="folder__icon" aria-hidden="true">{item.icon}</span>
			<span className="folder__name">{item.label}</span>
			{item.count ? (
				<span className="folder__count" aria-label={`${item.count} unread`}>
					{item.count > 999 ? '999+' : item.count}
				</span>
			) : null}
		</button>
	);

	return (
		<nav className="pane folders" aria-label="Mailboxes">
			<header className="pane__header">
				<img className="pane__logo" src={logo} alt="Keystone Management" />
			</header>

			<div style={{ padding: '12px 12px 8px' }}>
				<Button block size="lg" onClick={onCompose}>
					✎ Compose
				</Button>
			</div>

			<div style={{ flex: 1, paddingBottom: 8 }}>
				{mailboxes.primary.map(entry)}

				{mailboxes.more.length > 0 && (
					<>
						<button className="folder folder--toggle" aria-expanded={showMore} onClick={toggleMore}>
							<span className="folder__icon" aria-hidden="true">{showMore ? '▴' : '▾'}</span>
							<span className="folder__name">{showMore ? 'Less' : 'More'}</span>
						</button>
						{showMore && mailboxes.more.map(entry)}
					</>
				)}
			</div>

			<footer className="folders__footer">
				<span className="folders__email" title={email}>{email}</span>
				<div style={{ display: 'flex', gap: 8 }}>
					<Button variant="ghost" size="sm" onClick={onSettings}>Settings</Button>
					<Button variant="ghost" size="sm" onClick={onSignOut}>Sign out</Button>
				</div>
			</footer>
		</nav>
	);
}
